/**
 * Widget auth (SEC-40). The SDK holds no credential of its own.
 *
 * - Signed-in host (the Traderverse app): the host passes a token provider,
 *   e.g. core's `getAccessToken`, and the SDK calls the shared gateway's
 *   signed-in lanes with `Authorization: Bearer <Keycloak token>`.
 * - Public embed (no provider, or the provider returns nothing): the SDK calls
 *   the gateway's anonymous lanes (`/public/`), which are rate limited per IP.
 *
 * Facts routes that need a server-side credential (symbols search, chartapi,
 * facts-news) go through the gateway. Open Facts routes (`/markets/api/*` GETs)
 * are called directly, with no credential.
 *
 *   // ESM host
 *   import { configureWidgetAuth } from '@traderverse/widget-sdk';
 *   configureWidgetAuth({ getAccessToken, apiOrigin: import.meta.env.VITE_API_ORIGIN });
 *
 *   // script-tag host
 *   window.TraderverseWidgetAuth.configure({ getAccessToken: () => keycloak.token });
 */

export type AccessTokenProvider = () => string | null | undefined | Promise<string | null | undefined>;

export interface WidgetAuthOptions {
  /** Returns the signed-in user's access token, or nothing when signed out. */
  getAccessToken?: AccessTokenProvider | null;
  /** LAPI origin hosting the gateway at `{apiOrigin}/search/` (e.g. https://lapi-dev.traderverse.io). */
  apiOrigin?: string | null;
}

const DEV_API_ORIGIN = 'https://lapi-dev.traderverse.io';
const PROD_API_ORIGIN = 'https://lapi.traderverse.io';

let state: WidgetAuthOptions = {};

/** Set (or update) the host's token provider and API origin. */
export function configureWidgetAuth(options: WidgetAuthOptions): void {
  state = { ...state, ...options };
}

/** Back to defaults (no provider, derived origin). */
export function resetWidgetAuth(): void {
  state = {};
}

const stripSlash = (s: string): string => s.replace(/\/+$/, '');

function globalOrigin(): string {
  const g = typeof window !== 'undefined' ? (window as unknown as { TRADERVERSE_API_ORIGIN?: unknown }).TRADERVERSE_API_ORIGIN : undefined;
  return typeof g === 'string' ? g.trim() : '';
}

/** Configured origin, then `window.TRADERVERSE_API_ORIGIN`, then dev/prod by the page host. */
export function resolveApiOrigin(): string {
  const configured = (state.apiOrigin ?? '').trim() || globalOrigin();
  if (configured) return stripSlash(configured);
  const host = typeof location !== 'undefined' ? location.hostname : '';
  const isDev = !host || host === 'localhost' || host === '127.0.0.1' || /(^|[.-])dev[.-]/.test(host) || host.endsWith('.localhost');
  return isDev ? DEV_API_ORIGIN : PROD_API_ORIGIN;
}

/** `{apiOrigin}/search/v1`: the shared gateway (lapi-app-search) behind nginx's `/search/` prefix. */
export function gatewayBase(): string {
  return `${resolveApiOrigin()}/search/v1`;
}

/** The host's token, or null (signed out, no provider, or the provider failed). */
export async function resolveAccessToken(): Promise<string | null> {
  const provider = state.getAccessToken;
  if (typeof provider !== 'function') return null;
  try {
    const t = await provider();
    return typeof t === 'string' && t.trim() ? t.trim() : null;
  } catch {
    return null;
  }
}

export type GatewayFamily = 'chart' | 'news';

const FACTS_HOST_RE = /^facts(-dev)?\.traderverse\.io$/i;

/**
 * Facts URL -> gateway family + upstream path, for the routes that need a
 * server-side credential. Null for everything else (called directly).
 */
export function factsGatewayRoute(url: string): { family: GatewayFamily; path: string; search: string } | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  if (!FACTS_HOST_RE.test(u.hostname)) return null;
  const p = u.pathname;
  const strip = (prefix: string) => p.slice(prefix.length).replace(/^\/+/, '');
  if (p.startsWith('/markets/api/v3/chartapi/')) return { family: 'chart', path: strip('/markets/api/v3/chartapi/'), search: u.search };
  if (p.startsWith('/chartapi/')) return { family: 'chart', path: strip('/chartapi/'), search: u.search };
  if (p.startsWith('/news/')) return { family: 'news', path: strip('/news/'), search: u.search };
  return null;
}

/** Names of request headers and query keys that carried a credential before SEC-40; always dropped. */
export const CREDENTIAL_HEADERS = ['authorization', 'x-api-key'] as const;
export const CREDENTIAL_PARAMS = ['auth_token'] as const;

export interface PreparedRequest {
  url: string;
  headers: Record<string, string>;
}

/**
 * Final URL + headers for a GET. Credential headers and query keys from the
 * caller are removed; a Bearer is added only for the gateway's signed-in lanes.
 */
export async function prepareRequest(url: string, headers: Record<string, string> = {}): Promise<PreparedRequest> {
  const clean: Record<string, string> = {};
  for (const [k, v] of Object.entries(headers)) {
    if (!(CREDENTIAL_HEADERS as readonly string[]).includes(k.toLowerCase())) clean[k] = v;
  }
  let finalUrl = url;
  try {
    const u = new URL(url);
    for (const k of CREDENTIAL_PARAMS) u.searchParams.delete(k);
    finalUrl = u.toString();
  } catch {
    // not an absolute URL: leave it
  }

  const route = factsGatewayRoute(finalUrl);
  if (!route) return { url: finalUrl, headers: clean };

  const token = await resolveAccessToken();
  const lane = token ? route.family : `public/${route.family}`;
  const out = `${gatewayBase()}/market/${lane}/${route.path}${route.search}`;
  if (token) clean.Authorization = `Bearer ${token}`;
  return { url: out, headers: clean };
}

/** Asset search URL + headers: signed-in lane with a token, anonymous lane without. */
export async function prepareAssetSearch(query: URLSearchParams): Promise<PreparedRequest> {
  const token = await resolveAccessToken();
  const path = token ? 'search/assets' : 'search/public/assets';
  const headers: Record<string, string> = { accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return { url: `${gatewayBase()}/${path}?${query.toString()}`, headers };
}
